/**
 * DiagramTable Component
 *
 * The table view for the Diagram_Browser (the alternative to the grid). Each
 * row shows the Diagram_Title, Diagram_Folder, last editor, relative
 * last-edited time (with the exact time in a tooltip), and an "Editing: {name}"
 * badge when someone holds the Edit_Lock. Dynamic-width text columns use
 * `TruncatedCell`. See Requirements 9.2 and 9.3.
 */

import { formatDistanceToNow } from "date-fns"
import { Pencil } from "lucide-react"
import type { DiagramSummary } from "@/services/diagramsService"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { TruncatedCell } from "@/components/shared/TruncatedCell"
import DiagramKindBadge from "@/components/diagrams/DiagramKindBadge"
import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import { formatDate } from "@/utils/dateUtils"

/** Props for the DiagramTable component. */
interface DiagramTableProps {
  /** The diagrams to display, already filtered and sorted by the parent. */
  diagrams: DiagramSummary[]
  /** Called when the user opens a diagram in the editor. */
  onOpen: (diagram: DiagramSummary) => void
  /**
   * Renders the actions menu (task 5.4) for a given diagram. Optional so the
   * table renders before the actions task lands.
   */
  renderActions?: (diagram: DiagramSummary) => React.ReactNode
}

/**
 * Table view of diagrams.
 *
 * @param props - Component props.
 * @param props.diagrams - The diagrams to display (filtered and sorted).
 * @param props.onOpen - Handler to open a diagram in the editor.
 * @param props.renderActions - Optional per-diagram actions menu renderer.
 * @returns The rendered table element.
 */
export default function DiagramTable({
  diagrams,
  onOpen,
  renderActions,
}: DiagramTableProps): React.JSX.Element {
  return (
    <div className="h-full overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[34%]">Title</TableHead>
            <TableHead className="w-[12%]">Kind</TableHead>
            <TableHead className="w-[18%]">Folder</TableHead>
            <TableHead className="w-[18%]">Last editor</TableHead>
            <TableHead className="w-[18%]">Last edited</TableHead>
            {renderActions !== undefined && (
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {diagrams.map((diagram) => {
            const relativeEdited: string = formatDistanceToNow(new Date(diagram.updatedAt), {
              addSuffix: true,
            })
            const exactEdited: string = formatDate(diagram.updatedAt)
            return (
              <TableRow
                key={diagram.id}
                onClick={() => onOpen(diagram)}
                className="cursor-pointer"
              >
                <TableCell className="max-w-0">
                  <div className="flex items-center gap-2">
                    <TruncatedCell value={diagram.title} className="font-medium" />
                    {diagram.lock !== null && (
                      <Badge variant="secondary" className="shrink-0 gap-1">
                        <Pencil className="size-3" aria-hidden="true" />
                        <span className="max-w-24 truncate">
                          Editing: {diagram.lock.holderName}
                        </span>
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <DiagramKindBadge kind={diagram.kind} />
                </TableCell>
                <TableCell className="max-w-0">
                  <TruncatedCell value={diagram.folder ?? "Unfiled"} />
                </TableCell>
                <TableCell className="max-w-0">
                  <TruncatedCell value={diagram.updatedByName} />
                </TableCell>
                <TableCell>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-default">{relativeEdited}</span>
                    </TooltipTrigger>
                    <TooltipContent>{exactEdited}</TooltipContent>
                  </Tooltip>
                </TableCell>
                {renderActions !== undefined && (
                  // Stop row-open when interacting with the actions menu.
                  <TableCell onClick={(event) => event.stopPropagation()}>
                    {renderActions(diagram)}
                  </TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
